// Please write Jest Tests, when you use this functions

import {generatePath} from "react-router";
import {getCurrentDate} from "@/lib/date-utils";
import {Base64} from "js-base64";
import React from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {useTranslation} from "react-i18next";
import {CommonI18N} from "@/translations";
// Importing muiLocale also registers the Day.js locales it names, so formatDateTime works outside
// the rendered LocaleProvider too (operator pages render inside it, unit tests do not).
import {resolveDayjsLocale} from "@/app/locale/muiLocale";

// Laden des duration-Plugins
dayjs.extend(duration);
// localizedFormat backs the locale-aware "L" token used by formatDateTime. Without the plugin dayjs
// emits the token verbatim ("L 03:04:05"); registering it here keeps the format contract independent
// of the side-effect import in @mui/x-date-pickers/AdapterDayjs.
dayjs.extend(localizedFormat);

/**
 * Formats a given Date object into a localized date and time string.
 *
 * The i18next code is translated to a Day.js locale name by `resolveDayjsLocale` - the single
 * resolution point of the application. Passing the raw code would give `"en"` the built-in US
 * order `MM/DD/YYYY`, while the English convention here is the British one (`DD/MM/YYYY`, ADR-015).
 *
 * @param {Date} date - The date object to format.
 * @param {string} [locale="de"] - The optional i18next language code to format the date in. Defaults to "de" (German).
 * @returns {string} The formatted date and time string in the specified locale.
 */
export const formatDateTime = (date: Date, locale: string = "de"): string =>
    dayjs(date).locale(resolveDayjsLocale(locale)).format("L HH:mm:ss");

/**
 * Formats an ISO date-time string for display in the UI.
 *
 * Resolves the date order through `resolveDayjsLocale` and the locale-aware `L` token, exactly like
 * `formatDateTime` above — a hardcoded `DD.MM.YYYY` pattern would render the German order even when
 * the UI runs in English. Returns an empty string for `undefined`/`null` input, so it can be used
 * directly in JSX.
 *
 * @param {string | undefined | null} iso - The ISO date-time string to format.
 * @param {string} [locale="de"] - Optional i18next language code. Defaults to "de" (German).
 * @returns {string} The localized date-time string ("DD.MM.YYYY HH:mm" for German, "DD/MM/YYYY HH:mm"
 * for English), or an empty string when no input is provided.
 */
export const formatIsoDateTime = (iso: string | undefined | null, locale: string = "de"): string => {
    if (!iso) return "";
    const d = dayjs(iso);
    return d.isValid() ? d.locale(resolveDayjsLocale(locale)).format("L HH:mm") : "";
};

/**
 * Formats an ISO date string (YYYY-MM-DD) for display in the UI.
 *
 * Resolves the date order through `resolveDayjsLocale` and the locale-aware `L` token, exactly like
 * `formatDateTime` above — a hardcoded `DD.MM.YYYY` pattern would render the German order even when
 * the UI runs in English. Returns an empty string for `undefined`/`null`/empty input, so it can be
 * used directly in JSX.
 *
 * @param {string | undefined | null} iso - The ISO date string to format.
 * @param {string} [locale="de"] - Optional i18next language code. Defaults to "de" (German).
 * @returns {string} The localized date string ("DD.MM.YYYY" for German, "DD/MM/YYYY" for English),
 * or an empty string when no input is provided.
 */
export const formatIsoDate = (iso: string | undefined | null, locale: string = "de"): string => {
    if (!iso) return "";
    const d = dayjs(iso);
    return d.isValid() ? d.locale(resolveDayjsLocale(locale)).format("L") : "";
};

/**
 * Formats a duration given in seconds into a localized string representation.
 *
 * @param {number} seconds - The total duration in seconds to be formatted.
 * @param {Function} t - Translation function for localization. Typically obtained
 * from a translation library such as `useTranslation`, it is responsible for
 * translating the labels for hours, minutes, and seconds.
 * @returns {string} A formatted string representing the duration, with appropriate
 * hour, minute, and second labels, localized using the provided translation function.
 */
export const formatDuration = (seconds: number, t: ReturnType<typeof useTranslation>["t"]): string => {
    const d = dayjs.duration(seconds, "seconds");

    const hours = Math.floor(d.asHours());
    const minutes = d.minutes();
    const secs = d.seconds();

    let result = "";

    if (hours > 0) {
        result += `${hours} ${t(CommonI18N.field.hour)} `;
    }

    if (minutes > 0 || hours > 0) {
        result += `${minutes} ${t(CommonI18N.field.minute)} `;
    }

    result += `${secs} ${t(CommonI18N.field.second)}`;

    return result.trim();
};

export const autoCompleteOff = "off";

export type FieldError = {
    message?: string;
    [key: string]: any; // any is intentional: allows nested FieldError shapes
};

export type ErrorsShape<T> = {
    [K in keyof T]?: T[K] extends object
        ? ErrorsShape<T[K]> // Recursively transform sub-objects
        : FieldError; // Replace simple fields with a different type (e.g., any)
};

export type IsTEqual<T, U> = (<G>() => G extends T ? 1 : 2) extends <G>() => G extends U ? 1 : 2 ? true : false;

/**
 * Logs a message to the console if the environment is not production.
 *
 * @param message   The message to be logged.
 * @param optionalParams    Additional parameters to be logged.
 */
export const trace = (message: string, ...optionalParams: any[]) => {
    if (!isProductionCode) {

        console.log(message, ...optionalParams);
    }
};

export const isProductionCode = process.env.NODE_ENV === "production";

/**
 * Formats a given amount as a currency string based on the specified locale.
 *
 * @param {number | null} [amount] - The amount to be formatted. If the amount is undefined, null, or NaN, the function returns null.
 * @param {string} [locale="de"] - The locale to be used for formatting. Defaults to "de" (German).
 * @returns {string | null} - The formatted currency string or null if the amount is invalid.
 */
export const formatCurrency = (amount?: number | null, locale: string = "de"): string | null => {
    if (amount === undefined || amount === null || Number.isNaN(amount)) {
        return null;
    }
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: getCurrencyCode(locale)
    }).format(amount);
};

/**
 * Parses a currency string into a number based on the specified locale.
 *
 * @param {string} locale - The locale to be used for parsing.
 * @returns {(currencyString?: string | null) => number | null} - A function that takes a currency string and returns the parsed number or null if the input is invalid.
 */
export const parseCurrency =
    (locale: string): ((currencyString?: string | null) => number | null) =>
        (currencyString?: string | null): number | null => {
            if (!currencyString || currencyString.trim() === "") {
                return null;
            }

            const {thousandSeparator, decimalSeparator} = getSeparators(locale);
            const currencySymbol = getCurrencySymbol(locale);

            const escapedThousandSeparator = thousandSeparator?.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
            if (escapedThousandSeparator === undefined || decimalSeparator === undefined) {
                return null;
            }

            // Entfernen des Währungssymbols und Leerzeichen
            const cleanedString = currencyString.replaceAll(new RegExp(String.raw`[${currencySymbol}\s]`, "g"), "");

            const numericString = cleanedString
                .replaceAll(new RegExp(escapedThousandSeparator, "g"), "")
                .replace(decimalSeparator, ".");

            const parsedNumber = Number.parseFloat(numericString);

            return Number.isNaN(parsedNumber) ? null : parsedNumber;
        };

/**
 * Returns the currency code based on the specified locale.
 *
 * @param {string} locale - The locale to determine the currency code.
 * @returns {string} - The currency code for the given locale.
 */
export function getCurrencyCode(locale: string): string {
    // TODO: Add more currency codes for different locales
    return "EUR";
}

/**
 * Returns the currency symbol based on the specified locale.
 *
 * @param {string} locale - The locale to determine the currency symbol.
 * @returns {string} - The currency symbol for the given locale.
 */
export function getCurrencySymbol(locale: string): string {
    // TODO: Add more currency symbols for different locales
    return "€";
}

/**
 * Retrieves the thousand and decimal separators for a given locale.
 *
 * @param {string} locale - The locale to determine the separators.
 * @returns {{ thousandSeparator: string | undefined, decimalSeparator: string | undefined }} - An object containing the thousand and decimal separators.
 */
export function getSeparators(locale: string): {
    thousandSeparator: string | undefined;
    decimalSeparator: string | undefined;
} {
    const formatter = new Intl.NumberFormat(locale);
    const parts = formatter.formatToParts(1234567.89);
    const thousandSeparator = parts.find((part) => part.type === "group")?.value;
    const decimalSeparator = parts.find((part) => part.type === "decimal")?.value;

    return {thousandSeparator, decimalSeparator};
}

/**
 * Formats a given number as an integer string based on the specified locale.
 *
 * @param {number | null} [number] - The number to be formatted. If the number is undefined, null, or NaN, the function returns null.
 * @param {string} [locale="de"] - The locale to be used for formatting. Defaults to "de" (German).
 * @returns {string | null} - The formatted integer string or null if the number is invalid.
 */
export const formatInteger = (number?: number | null, locale: string = "de"): string | null => {
    if (number === undefined || number === null || Number.isNaN(number)) {
        return null;
    }
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(number);
};

/**
 * Formats a total/subset count pair as "total (subset)", using locale-specific thousand separators.
 *
 * Missing (`undefined`/`null`) values are treated as `0`, so the function always returns a
 * non-null string — safe to use directly in JSX (e.g. table cell renderers).
 *
 * @param {number} [total] - The total count. Treated as `0` when `undefined`/`null`.
 * @param {number} [subset] - The subset count. Treated as `0` when `undefined`/`null`.
 * @param {string} [locale="de"] - The locale to be used for formatting. Defaults to "de" (German).
 * @returns {string} - The formatted "total (subset)" string.
 */
export const formatCountWithSubset = (total?: number, subset?: number, locale: string = "de"): string => {
    const safeTotal = total === undefined || total === null || Number.isNaN(total) ? 0 : total;
    const safeSubset = subset === undefined || subset === null || Number.isNaN(subset) ? 0 : subset;
    return `${formatInteger(safeTotal, locale)} (${formatInteger(safeSubset, locale)})`;
};

/**
 * Formats a given number according to the specified locale and fraction digits.
 *
 * @param {number | null} [number] - The number to format. If undefined, null, or not a valid number, the function returns null.
 * @param {number} [fraction] - The number of fraction digits to include in the formatted output. Defaults to 0 if not specified.
 * @param {string} [locale="de"] - The locale used for formatting. Defaults to "de" (German locale) if not specified.
 * @returns {string | null} - The formatted number as a string, or null if the input number is invalid.
 */
export const formatNumber = (number?: number | null, fraction?: number, locale: string = "de"): string | null => {
    if (number === undefined || number === null || Number.isNaN(number)) {
        return null;
    }
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: fraction ?? 0,
        maximumFractionDigits: fraction ?? 0
    }).format(number);
};

/**
 * Parses a numeric string into a number based on the specified locale.
 *
 * @param {string} locale - The locale to be used for parsing.
 * @returns {(value?: number | null, originalValue?: number | string | null) => number | null} - A function that takes a numeric string and returns the parsed number or null if the input is invalid.
 */
export const parseNumericString =
    (locale: string): ((value?: number | null, originalValue?: number | string | null) => number | null) =>
        (value?: number | null, originalValue?: number | string | null): number | null => {
            if (typeof originalValue !== "string") {
                if (typeof originalValue === "number") {
                    return originalValue;
                }
                return value ?? null;
            }

            const decimalSeparator = getSeparators(locale).decimalSeparator ?? "";
            const thousandSeparator = getSeparators(locale).thousandSeparator ?? "";

            // Remove thousand separators
            const cleanedString = originalValue.replaceAll(new RegExp(`\\${thousandSeparator}`, "g"), "");

            // Replace decimal separator with dot for standardization
            const normalizedString = cleanedString.replace(decimalSeparator, ".");

            const parsed = Number.parseFloat(normalizedString);
            return Number.isNaN(parsed) ? null : parsed;
        };

/**
 * Capitalizes the first letter of the given text.
 *
 * @param {string} text - The text to be capitalized.
 * @returns {string} - The text with the first letter capitalized.
 */
export const capitalizeFirstLetter = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Capitalizes the first letter of each sentence in the given text after a period.
 *
 * @param {string} sentence - The text to be processed.
 * @returns {string} - The processed text with the first letter of each sentence capitalized.
 */
export const capitalizeAfterPeriod = (sentence: string): string => {
    // Split the sentence into parts based on the period
    const parts = sentence.split(". ");

    // Iterate over each part
    for (let i = 0; i < parts.length; i++) {
        // Trim any leading/trailing whitespace
        parts[i] = parts[i].trim();

        // Capitalize the first letter of the part
        if (parts[i].length > 0) {
            parts[i] = parts[i][0].toUpperCase() + parts[i].slice(1);
        }
    }

    // Join the parts back together with a period and a space
    return parts.join(". ");
};

/**
 * Capitalizes the first letter of each word in the given text.
 *
 * @param {string} text - The text to be processed.
 * @returns {string} - The processed text with the first letter of each word capitalized.
 */
export const capitalizeEachWord = (text: string): string =>
    text
        .split(" ")
        .map((word) => capitalizeFirstLetter(word))
        .join(" ");

/**
 * Returns a human-readable relative time label with plural suffix.
 *
 * @param {number} count - The numeric value.
 * @param {string} unit - The singular unit name (e.g. "minute").
 * @returns {string} - E.g. "3 minutes ago" or "1 minute ago".
 */
const relativeTimeLabel = (count: number, unit: string): string =>
    `${count} ${unit}${count === 1 ? "" : "s"} ago`;

/**
 * Returns a human-readable relative time string based on the given ISO date.
 *
 * The function calculates the difference between the current date and the given ISO date,
 * and returns a string that represents this difference in a human-readable format.
 * For example, if the given date is 5 minutes ago, the function will return "5 minutes ago".
 * The function handles different time ranges, such as seconds, minutes, hours, days, weeks, months, and years.
 *
 * @param {string} isoDate - The ISO date string to be converted to a relative time string.
 * @returns {string} - A string representing the relative time from the given date to now.
 */
export const getRelativeTime = (isoDate: string): string => {
    const now = getCurrentDate();
    const past = new Date(isoDate);
    const diffInSeconds = (now.getTime() - past.getTime()) / 1000;

    if (diffInSeconds < 60) {
        return "just now";
    }
    if (diffInSeconds < 3600) {
        return relativeTimeLabel(Math.floor(diffInSeconds / 60), "minute");
    }
    if (diffInSeconds < 86400) {
        return relativeTimeLabel(Math.floor(diffInSeconds / 3600), "hour");
    }
    if (diffInSeconds < 604800) {
        return relativeTimeLabel(Math.floor(diffInSeconds / 86400), "day");
    }
    if (diffInSeconds < 2419200) {
        return relativeTimeLabel(Math.floor(diffInSeconds / 604800), "week");
    }
    if (diffInSeconds < 29030400) {
        return relativeTimeLabel(Math.floor(diffInSeconds / 2419200), "month");
    }
    return relativeTimeLabel(Math.floor(diffInSeconds / 29030400), "year");
};

/**
 * Converts a hyphen-separated string to title case.
 *
 * This function splits the input text by hyphens, capitalizes the first letter of each word,
 * and then joins the words back together with spaces.
 *
 * Example:
 * - Input: "hello-world-example"
 * - Output: "Hello World Example"
 *
 * @param {string} text - The hyphen-separated text to be converted.
 * @returns {string} - The converted text in title case.
 */
export const hyphenToTitleCase = (text: string): string => {
    const words = text.split("-");
    return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};
/**
 * Checks if the given value is a valid Date object.
 *
 * This function verifies if the input is an instance of Date and that it represents a valid date.
 *
 * @param {unknown} d - The value to be checked.
 * @returns {boolean} - True if the value is a valid Date object, false otherwise.
 */
export const isDate = (d: unknown): boolean => d instanceof Date && !Number.isNaN(d.getTime());

/**
 * Converts a number to a three-digit string.
 *
 * This function takes a number and converts it to a string with at least three digits,
 * padding with leading zeros if necessary. If the number is undefined, null, less than 0, or greater than 999,
 * the function returns null.
 *
 * @param {number | null} [num] - The number to be converted to a three-digit string.
 * @returns {string | null} - The three-digit string or null if the input is invalid.
 */
export function convertToThreeDigitString(num?: number | null): string | null {
    if (num === undefined || num === null || num < 0 || num > 999) {
        return null;
    }
    return num.toString().padStart(3, "0");
}

/**
 * Safely generates a path using the given path template and parameters.
 *
 * This function attempts to generate a path using the `generatePath` function from `react-router`.
 * If an error occurs during path generation, it logs the error and returns a fallback path ("#").
 *
 * @param {string} path - The path template to be used for generating the path.
 * @param {Record<string, string | number>} [params] - The parameters to be used for generating the path.
 * @returns {string} - The generated path or a fallback path ("#") if an error occurs.
 */
export const generatePathSafe = (path: string, params?: Record<string, string | number>): string => {
    try {
        return generatePath(path, params);
    } catch (error) {
        trace(`generatePathSafe: failed to generate path for "${path}"`, error);
        return "#";
    }
};

/**
 * Trims the given string and returns it, or returns null if the string is empty or undefined.
 *
 * This function trims leading and trailing whitespace from the input string.
 * If the input string is undefined, null, or results in an empty string after trimming, the function returns null.
 *
 * @param {string | undefined | null} value - The string to be trimmed.
 * @returns {string | null} - The trimmed string or null if the input is empty or undefined.
 */
export function trimOrNull(value: string | undefined | null): string | null {
    if (!value) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Removes the word after the numeric string.
 *
 * @param {string | null | undefined} value - The input string containing a numeric value followed by a word.
 * @returns {string | null} - The numeric part of the input string or null if the input is null or undefined.
 */
export function extractNumericStringValue(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmedValue = value.trim();
    const match = /^\d+(\.\d+)?/.exec(trimmedValue);
    return match ? match[0] : trimmedValue;
}

/**
 * Encodes a password using Base64 encoding.
 *
 * This function takes a password string and encodes it using the Base64 encoding scheme.
 *
 * @param {string} password - The password to be encoded.
 * @returns {string} - The Base64 encoded password.
 */
export function encodePasswordBase64<T = string | null>(password: T): T {
    if (typeof password === "string") {
        return Base64.encode(password) as T;
    }
    return password;
}

/**
 * Safely calls a function and logs any errors that occur.
 *
 * This function attempts to call the provided function `fnc`. If an error occurs during the function call,
 * it catches the error and logs it to the console.
 *
 * @param {Function} fnc - The function to be called safely.
 * @returns {*} - The return value of the function `fnc`, or undefined if an error occurs.
 */
export const safeCall = (fnc: Function): any => {
    try {
        return fnc?.();
    } catch (e) {
        console.error(e);
    }
};

/**
 * Extracts a value from an originalEvent wrapper if present.
 *
 * @param {string} name - The resolved event name.
 * @param {Record<string, unknown>} originalEvent - The originalEvent object to inspect.
 * @returns {[string, unknown] | null} - The extracted name-value pair, or null.
 */
const extractFromOriginalEvent = (name: string, originalEvent: Record<string, unknown>): [string, unknown] | null => {
    if (Object.hasOwn(originalEvent, "value")) {
        return [name, originalEvent.value];
    }
    const target = originalEvent.target as Record<string, unknown> | undefined;
    if (target && Object.hasOwn(target, "value")) {
        return [name, target.value];
    }
    return null;
};

/**
 * Extracts the name and value from an event object.
 *
 * This function attempts to extract the name and value from the provided event object `e`.
 * It handles various event types, including checkbox, radio, and standard input events.
 * If the event does not contain a name or value, it logs an error and returns null.
 *
 * @param {unknown} e - The event object from which to extract the name and value.
 * @returns {[string, unknown] | null} - A tuple containing the name and value, or null if extraction fails.
 */
export const extractEvent = (e: unknown): [string, unknown] | null => {
    if (e == null || typeof e !== "object") {
        return null;
    }
    const ev = e as Record<string, unknown>;
    const target = ev.target as Record<string, unknown> | undefined;
    const originalEvent = ev.originalEvent as Record<string, unknown> | undefined;
    const originalEventTarget = originalEvent?.target as Record<string, unknown> | undefined;
    const rawName = ev.name || target?.name || originalEvent?.name || originalEventTarget?.name;
    if (rawName == null || typeof rawName !== "string") {
        return null;
    }
    const name: string = rawName;
    if (target) {
        if (target.type === "checkbox") {
            return [name, target.checked];
        }
        if (target.type === "radio") {
            return [name, target.value];
        }
    }
    if (Object.hasOwn(ev, "value")) {
        return [name, ev.value];
    }
    if (target && Object.hasOwn(target, "value")) {
        return [name, target.value];
    }
    if (originalEvent) {
        return extractFromOriginalEvent(name, originalEvent);
    }
    return null;
};

/**
 * Converts a value to a Date object if the original value is a non-empty string.
 *
 * This function checks if the `originalValue` is a non-empty string. If it is, it attempts to parse it into a Date object.
 * If the parsing results in an invalid date, it returns null. Otherwise, it returns the parsed Date object.
 * If the `originalValue` is not a non-empty string, it returns the `value` as is.
 *
 * @param {any} value - The value to be returned if the original value is not a string.
 * @param {any} originalValue - The original value to be checked and possibly converted to a Date.
 * @returns {Date | null | undefined} - The parsed Date object, null if the parsing fails, or the original value if it is not a string.
 */
export const convertDate = (value: any, originalValue: any): Date | null | undefined => {
    if (typeof originalValue === "string" && originalValue.trim() !== "") {
        const parsedDate = new Date(originalValue);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    return value; // Pass through the value if already a Date
};

let globalUIDCounter = 1;
export const nextUID = () => {
    globalUIDCounter += 1;
    return globalUIDCounter;
};

export const makeUID = (name: string) => `${name || "uid"}-${nextUID()}`;

/**
 * Ensures that the given value is returned as an array.
 *
 * This function checks if the input value is null or undefined and returns an empty array in that case.
 * If the input value is already an array, it returns the value as is.
 * Otherwise, it wraps the value in an array and returns it.
 *
 * @template T - The type of the elements in the array.
 * @param {T | T[] | null | undefined} value - The value to be ensured as an array.
 * @returns {T[]} - The input value wrapped in an array, or an empty array if the input is null or undefined.
 */
export const ensureArray = <T>(value: T | T[] | null | undefined): T[] => {
    if (value == null) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

/**
 * Truncates a string to a specified length and appends an ellipsis if necessary.
 *
 * This function checks if the input string is null or undefined, or if its length is less than or equal to the specified number.
 * If any of these conditions are met, it returns the original string. Otherwise, it truncates the string to the specified length
 * and appends an ellipsis ("...") to the end.
 *
 * @param {string | undefined} str - The string to be truncated.
 * @param {number} num - The maximum length of the truncated string.
 * @returns {string | undefined} - The truncated string with an ellipsis, or the original string if no truncation is needed.
 */
export const truncateString = (str: string | undefined, num: number) => {
    if (str == null || str.length <= num) {
        return str;
    }
    return `${str.slice(0, num)}...`;
};

export const formatNumberLeadingZero = (number: number, length: number): string => {
    if (number === undefined || number === null || Number.isNaN(number)) {
        return "";
    }
    const str = number.toString();
    return str.length < length ? str.padStart(length, "0") : str;
};

export const scrollToNextSection = (sr: React.RefObject<any>) => {
    sr?.current?.scrollIntoView({behavior: "smooth"});
};

export const findScrollParent = (node) => {
    if (node) {
        const style = globalThis.window.getComputedStyle(node);
        if (style) {
            if (style["overflow-y"] === "scroll" || style["overflow-y"] === "auto") {
                return node;
            }

            if (style["overflow"] === "scroll" || style["overflow"] === "auto") {
                return node;
            }
        }
        return findScrollParent(node.parentNode);
    }
    return node;
};
